import fs from "node:fs";
import path from "node:path";
import { assetSearchDirs } from "@skaile/workspaces/core";
import { parse as parseYaml } from "yaml";
import { parseFlowDefinition } from "../contract/normalize.js";
import type { FlowDefinition } from "./types.js";

/**
 * The filename suffixes a flow file may carry, in probe order. `.json` is the
 * legacy bare form and stays last so an explicit `.flow.json` wins a tie.
 *
 * @docLink packages/flow-engine/concepts#flow-loading
 */
export const FLOW_FILE_EXTENSIONS = [".flow.yaml", ".flow.yml", ".flow.json", ".json"] as const;

/** A discovered flow paired with the file it was loaded from. */
export interface FlowFileEntry {
  flow: FlowDefinition;
  path: string;
}

/**
 * The conventional on-disk locations of flow `flowId` inside one `flows/` search
 * dir: the flat `<dir>/<id><ext>` and the per-flow-directory
 * `<dir>/<id>/<id><ext>`, for every {@link FLOW_FILE_EXTENSIONS}. Existence is
 * NOT checked. Shared with the CLI's `findFlowFile` so the naming convention has
 * exactly one definition.
 *
 * @docLink packages/flow-engine/concepts#flow-loading
 */
export function flowFileCandidates(dirPath: string, flowId: string): string[] {
  return [
    ...FLOW_FILE_EXTENSIONS.map((ext) => path.join(dirPath, `${flowId}${ext}`)),
    ...FLOW_FILE_EXTENSIONS.map((ext) => path.join(dirPath, flowId, `${flowId}${ext}`)),
  ];
}

/** Matches `flow.yaml` as well as `<anything>.flow.yaml` (and `.yml` / `.json`). */
const FLOW_SHAPED_FILE = /(^|\.)flow\.(ya?ml|json)$/i;

/** How far below a per-flow directory a stray flow file is still reported. */
const STRAY_SCAN_DEPTH = 3;

function isDirectory(entry: fs.Dirent, fullPath: string): boolean {
  if (entry.isDirectory()) return true;
  if (!entry.isSymbolicLink()) return false;
  try {
    return fs.statSync(fullPath).isDirectory();
  } catch {
    return false;
  }
}

/**
 * First flow-shaped file at or below `dirPath`, used only to decide whether a
 * subdirectory that yielded no conventional flow deserves a warning. Deliberately
 * ignores bare `.json` so sibling assets (`package.json`, fixtures) stay silent.
 */
function findStrayFlowFile(dirPath: string, depth: number = STRAY_SCAN_DEPTH): string | undefined {
  let entries: fs.Dirent[];
  try {
    entries = fs.readdirSync(dirPath, { withFileTypes: true });
  } catch {
    return undefined;
  }
  for (const entry of entries) {
    if (!isDirectory(entry, path.join(dirPath, entry.name)) && FLOW_SHAPED_FILE.test(entry.name)) {
      return path.join(dirPath, entry.name);
    }
  }
  if (depth <= 1) return undefined;
  for (const entry of entries) {
    const full = path.join(dirPath, entry.name);
    if (!isDirectory(entry, full)) continue;
    if (entry.name.startsWith("_") || entry.name.startsWith(".") || entry.name === "node_modules") {
      continue;
    }
    const stray = findStrayFlowFile(full, depth - 1);
    if (stray) return stray;
  }
  return undefined;
}

/**
 * Load a single {@link FlowDefinition} from a YAML or JSON file on disk.
 *
 * Format detection is based on file extension:
 * - `.yaml` / `.yml` — parsed with the `yaml` library.
 * - Everything else  — parsed as JSON.
 *
 * @param filePath - Absolute path to the flow file.
 * @returns The parsed {@link FlowDefinition}.
 * @throws {Error} If the file does not exist, cannot be read, or fails to parse.
 * @docLink packages/flow-engine/concepts#flow-loading
 */
export function loadFlow(filePath: string): FlowDefinition {
  const raw = fs.readFileSync(filePath, "utf-8");
  const ext = path.extname(filePath).toLowerCase();
  const authored = ext === ".yaml" || ext === ".yml" ? parseYaml(raw) : JSON.parse(raw);
  const parsed = parseFlowDefinition(authored);
  if (!parsed.ok) {
    const details = parsed.errors
      .map((issue) => `  ${issue.path || "<root>"}: ${issue.message}`)
      .join("\n");
    throw new Error(`Invalid flow definition:\n${details}`);
  }
  return parsed.data;
}

/**
 * Load every flow in a `flows/` directory, in both supported shapes, keeping the
 * file each one came from.
 *
 * Two tiers, and nothing else:
 * 1. **Flat** — a file directly in `dirPath` ending in one of {@link FLOW_FILE_EXTENSIONS}.
 * 2. **Per-flow directory** — `<dir>/<sub>/<sub><ext>`; the file MUST be named after
 *    its directory, which is what lets a flow keep sibling assets (README, fixtures,
 *    prompts) without them being parsed as flows.
 *
 * A subdirectory that holds something flow-shaped but no conventional file is
 * **reported** through `onWarn` rather than dropped silently — silence is the
 * defect this contract exists to remove. Files/dirs prefixed with `_` are the
 * explicit opt-out and stay silent. Parse failures are warned about and skipped,
 * never thrown.
 *
 * @param dirPath - Absolute path to the directory to scan.
 * @param opts.onWarn - Sink for warnings; defaults to `stderr`. Pass a no-op on
 *   hot paths where a probe miss is normal (e.g. resolving an arbitrary CLI arg).
 * @docLink packages/flow-engine/concepts#flow-loading
 */
export function loadFlowEntriesFromDir(
  dirPath: string,
  opts: { onWarn?: (message: string) => void } = {},
): FlowFileEntry[] {
  const warn = opts.onWarn ?? ((message: string) => void process.stderr.write(`${message}\n`));

  let entries: fs.Dirent[];
  try {
    entries = fs.readdirSync(dirPath, { withFileTypes: true });
  } catch {
    return [];
  }

  const found: FlowFileEntry[] = [];
  const load = (filePath: string, label: string) => {
    try {
      const def = loadFlow(filePath);
      if (def.id && def.nodes && def.edges) found.push({ flow: def, path: filePath });
    } catch (err) {
      warn(`[agent-flow-engine] skipping ${label}: ${(err as Error).message}`);
    }
  };

  for (const entry of entries) {
    if (entry.name.startsWith("_")) continue;
    const full = path.join(dirPath, entry.name);

    if (!isDirectory(entry, full)) {
      if (FLOW_FILE_EXTENSIONS.some((ext) => entry.name.endsWith(ext))) load(full, entry.name);
      continue;
    }
    if (entry.name.startsWith(".") || entry.name === "node_modules") continue;

    const conventional = FLOW_FILE_EXTENSIONS.map((ext) =>
      path.join(full, `${entry.name}${ext}`),
    ).find((candidate) => fs.existsSync(candidate));
    if (conventional) {
      load(conventional, path.join(entry.name, path.basename(conventional)));
      continue;
    }

    const stray = findStrayFlowFile(full);
    if (stray) {
      warn(
        `[agent-flow-engine] ignoring ${stray}: a flow directory must hold ` +
          `${entry.name}.flow.yaml (or .flow.yml / .flow.json / .json)`,
      );
    }
  }

  return found;
}

/**
 * Load all flows from a directory — {@link loadFlowEntriesFromDir} without the
 * originating file paths. See it for the two supported layouts, the warning
 * contract, and the `_`-prefix opt-out.
 *
 * @param dirPath - Absolute path to the directory to scan.
 * @returns Array of successfully parsed {@link FlowDefinition} objects. Empty if the
 *   directory does not exist or contains no valid flow files.
 * @docLink packages/flow-engine/concepts#flow-loading
 */
export function loadFlowsFromDir(dirPath: string): FlowDefinition[] {
  return loadFlowEntriesFromDir(dirPath).map((entry) => entry.flow);
}

/**
 * Discover every flow under a content root, keeping the file each came from.
 *
 * Covers both content-root layouts (`<root>/flows/` and `<root>/<domain>/flows/`)
 * via `assetSearchDirs`, and within each of those both flow layouts via
 * {@link loadFlowEntriesFromDir}. De-duped by **declared id**, first search dir
 * wins — the same identity every other flow surface resolves by.
 *
 * @param aiResourcesRoot - Absolute path to a content root (`ai-assets/` or equivalent).
 * @docLink packages/flow-engine/concepts#flow-loading
 */
export function loadAllFlowEntries(
  aiResourcesRoot: string,
  opts: { onWarn?: (message: string) => void } = {},
): FlowFileEntry[] {
  if (!fs.existsSync(aiResourcesRoot)) return [];

  const byId = new Map<string, FlowFileEntry>();
  for (const { dir } of assetSearchDirs(aiResourcesRoot, "flows")) {
    for (const entry of loadFlowEntriesFromDir(dir, opts)) {
      if (!byId.has(entry.flow.id)) byId.set(entry.flow.id, entry);
    }
  }
  return [...byId.values()];
}

/**
 * Discover and load all flow definitions under a content root —
 * {@link loadAllFlowEntries} without the originating file paths.
 *
 * @param aiResourcesRoot - Absolute path to the `ai-assets/` directory (or equivalent
 *   resource root). Typically the `ai-assets/` folder at the monorepo root.
 * @returns Flat array of all successfully parsed {@link FlowDefinition} objects across
 *   all domains. Empty if the root directory does not exist.
 * @docLink packages/flow-engine/concepts#flow-loading
 */
export function loadAllFlows(aiResourcesRoot: string): FlowDefinition[] {
  return loadAllFlowEntries(aiResourcesRoot).map((entry) => entry.flow);
}
